// Validation utilities for study plans

/**
 * Calculate expected hours for ECTS credits.
 * Standard: 25-30 hours per ECTS.
 *
 * Task #330 (2026-07-03): Streng validering — 25-30 t/ECTS uansett størrelse.
 * Motsigelsen som Kent opplevde (4 timer + 0.1 ECTS) løses ved at timer-feltet
 * auto-oppdaterer ECTS-verdien når timer endres (StudyPlanForm.tsx), så
 * bruker aldri havner i en tilstand hvor ECTS og timer ikke stemmer overens.
 */
export function calculateExpectedHours(ects: number): { min: number; max: number } {
  return {
    min: Math.round(ects * 25 * 100) / 100,
    max: Math.round(ects * 30 * 100) / 100,
  };
}

/**
 * Validate if estimated hours align with ECTS
 */
export function validateECTSAlignment(ects: number, estimatedHours: number): {
  valid: boolean;
  message?: string;
} {
  const expected = calculateExpectedHours(ects);

  if (estimatedHours < expected.min) {
    return {
      valid: false,
      message: `Estimerte timer (${estimatedHours}) er for lavt for ${ects} kompetansepoeng (ECTS). Forventet: ${expected.min}-${expected.max} timer.`,
    };
  }

  if (estimatedHours > expected.max) {
    return {
      valid: false,
      message: `Estimerte timer (${estimatedHours}) er for høyt for ${ects} kompetansepoeng (ECTS). Forventet: ${expected.min}-${expected.max} timer.`,
    };
  }

  return { valid: true };
}

/**
 * Validate learning outcomes structure
 */
export function validateLearningOutcomes(outcomes: any): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!outcomes.knowledge || !Array.isArray(outcomes.knowledge)) {
    errors.push('Kunnskaper må være en liste');
  } else if (outcomes.knowledge.length === 0) {
    errors.push('Minst ett læringsutbytte for kunnskaper kreves');
  }

  if (!outcomes.skills || !Array.isArray(outcomes.skills)) {
    errors.push('Ferdigheter må være en liste');
  } else if (outcomes.skills.length === 0) {
    errors.push('Minst ett læringsutbytte for ferdigheter kreves');
  }

  if (!outcomes.competence || !Array.isArray(outcomes.competence)) {
    errors.push('Kompetanse må være en liste');
  } else if (outcomes.competence.length === 0) {
    errors.push('Minst ett læringsutbytte for kompetanse kreves');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Generate approval link
 */
export function generateApprovalLink(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
}
